import { FeedId, SubscribeStreamList } from 'features/streaming/types';
import { logger } from 'utils/logger';
import { ReceivingFeedStreamSendingState, RTCClient, ToggleMediaTemplate } from 'utils/webrtc';
import { batchToggleMediaTemplate, sendMessage } from 'utils/webrtc/messages';
import type { StreamMedia } from 'utils/webrtc/receiving/StreamMedia';

// Screenshare feeds share the receiving media list, but their receiving state is owned by the
// screenshare visibility sagas, not by pagination.
export const isScreenshareFeed = (feedId: FeedId) =>
  Boolean(RTCClient.userIdByScreensharingFeedId[feedId]);

export const isLocalFeed = (feedId: FeedId) =>
  Boolean(RTCClient.localFeeds[feedId]) ||
  feedId === RTCClient.publishingFeed.handle?.feedId ||
  feedId === RTCClient.screensharingFeed.handle?.feedId;

export const isPendingScreenshareSubscription = (feedId: FeedId) =>
  Boolean(RTCClient.receivingFeed.availableScreenshares[feedId]) &&
  !RTCClient.receivingFeed.media.subscribedFeeds[feedId];

export const pauseVideoStream = (
  videoStream: StreamMedia,
  streamsToConfigure: ToggleMediaTemplate,
  pausedStreams: StreamMedia[]
) => {
  const { subscriberMid } = videoStream;

  if (!subscriberMid || videoStream.receivingState === ReceivingFeedStreamSendingState.off) {
    return;
  }

  pausedStreams.push(videoStream);
  streamsToConfigure.push({ mid: subscriberMid, send: false });
};

export const flushMediaUpdates = async (
  streamsToConfigure: ToggleMediaTemplate,
  pausedStreams: StreamMedia[],
  streamsToSubscribe: SubscribeStreamList = []
) => {
  const feed = RTCClient.getJanusPlugin();
  const connection = RTCClient.receivingFeed.receiver?.connection;

  if (!feed || !connection) {
    return;
  }

  try {
    if (streamsToConfigure.length) {
      await sendMessage(feed, batchToggleMediaTemplate(streamsToConfigure));

      // Mark the pause locally only once the server has taken it: the off state is what makes
      // later reconciles skip the feed, so committing it on a send that never landed would leave
      // the feed streaming forever.
      pausedStreams.forEach((stream) =>
        stream.setReceivingState(ReceivingFeedStreamSendingState.off)
      );
    }

    if (streamsToSubscribe.length) {
      await RTCClient.receivingFeed.requestSubscription(connection.handle, streamsToSubscribe);
    }
  } catch (error) {
    logger
      .remote({ system: true, capture: 'layout' })
      .warn('Pagination media update failed, likely mid-reconnect', { error: String(error) });
  }
};

export const pauseVideoStreams = async (videoStreams: StreamMedia[]) => {
  const streamsToConfigure: ToggleMediaTemplate = [];
  const pausedStreams: StreamMedia[] = [];

  videoStreams.forEach((videoStream) =>
    pauseVideoStream(videoStream, streamsToConfigure, pausedStreams)
  );

  await flushMediaUpdates(streamsToConfigure, pausedStreams);
};
